import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle, X, Maximize2 } from '../lib/icons';
import { VideoCard, VideoItem, isYouTube, getYouTubeEmbedUrl } from './VideoCard';

const FAQ_VIDEOS: VideoItem[] = [
  {
    id: 'ps5-overheating',
    title: 'Why Is My PS5 Overheating?',
    sub: 'The real cause — and the fix',
    src: 'https://youtube.com/shorts/a_0c-PK63RY',
  },
  {
    id: 'ps5-hdmi',
    title: "Why Won't My PS5 Connect to the TV?",
    sub: 'Why HDMI ports fail so often',
    src: 'https://youtu.be/-QrgA0L5m9c',
  },
  {
    id: 'controller-drift',
    title: 'Why Is My Controller Drifting?',
    sub: 'Analog sticks explained',
    src: 'https://youtube.com/shorts/GFXFFoQktQI',
  },
  {
    id: 'switch-wont-charge',
    title: "Why Won't My Switch Charge?",
    sub: 'USB-C, M92, BQ — what broke?',
    src: 'https://youtube.com/shorts/eCX0vS6GXMw',
  },
  {
    id: 'already-opened',
    title: 'Do We Accept Pre-Opened Consoles?',
    sub: 'What to know before you bring it in',
    src: 'https://youtube.com/shorts/V9pX-49syFY',
  },
  {
    id: 'repair-vs-replace',
    title: 'Repair or Replace Your Console?',
    sub: 'How to decide',
    src: null,
  },
];

// ── Lightbox ─────────────────────────────────────────────────────────────────
const Lightbox = ({ vid, onClose }: { vid: VideoItem; onClose: () => void }) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const isYT = vid.src ? isYouTube(vid.src) : false;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      ref={backdropRef}
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
    >
      <div className="relative w-full max-w-sm mx-auto">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors flex items-center gap-1.5 text-sm font-medium"
        >
          <X className="h-4 w-4" /> Close
        </button>

        <div className="rounded-2xl overflow-hidden bg-black shadow-2xl aspect-[9/16]">
          {isYT ? (
            <iframe
              src={getYouTubeEmbedUrl(vid.src!, false)}
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              className="w-full h-full"
              title={vid.title}
            />
          ) : (
            <video
              src={vid.src!}
              controls
              autoPlay
              playsInline
              className="w-full h-full object-contain"
            />
          )}
        </div>

        <div className="mt-3 text-center">
          <p className="text-white font-bold text-sm">{vid.title}</p>
          <p className="text-[#FF1744] text-xs mt-0.5">{vid.sub}</p>
        </div>
      </div>
    </div>
  );
};

// ── Section ───────────────────────────────────────────────────────────────────
const VideoFAQ = () => {
  const [expanded, setExpanded] = useState<VideoItem | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);

  return (
    <section className="py-20 bg-[#0D0D0D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-full px-6 py-2 mb-6">
            <HelpCircle className="h-4 w-4 text-[#FF1744]" />
            <span className="text-[#FF1744] font-semibold text-sm">QUICK ANSWERS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">Got Questions?</h2>
          <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
            Short videos answering the most common console questions we hear every day
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {FAQ_VIDEOS.map(vid => (
            <div
              key={vid.id}
              className="relative group cursor-pointer"
              onClick={() => vid.src && setExpanded(vid)}
            >
              {/* Unmount the tile while its lightbox is open so the background audio stops */}
              {expanded?.id !== vid.id && (
                <VideoCard
                  vid={vid}
                  accentColor="#FF1744"
                  isActive={activeId === vid.id || (!!vid.src && isYouTube(vid.src))}
                  isMuted={muted}
                  onMuteToggle={() => setMuted(m => !m)}
                  onEnded={() => setActiveId(null)}
                  onActivate={() => setActiveId(vid.id)}
                />
              )}
              {/* Expand hint on hover for live videos */}
              {vid.src && !expanded && (
                <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center pointer-events-none">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 backdrop-blur-sm rounded-full p-3">
                    <Maximize2 className="h-5 w-5 text-white" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {expanded && (
        <Lightbox vid={expanded} onClose={() => setExpanded(null)} />
      )}
    </section>
  );
};

export default VideoFAQ;
